import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SecurityTab from './components/SecurityTab';
import DevicesTab from './components/DevicesTab';
import PreferencesTab from './components/PreferencesTab';
import PrivacyTab from './components/PrivacyTab';
import SessionsTab from './components/SessionsTab';

const AccountSettings = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('security');

  const tabs = [
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      component: SecurityTab
    },
    {
      id: 'devices',
      label: 'Devices',
      icon: 'Monitor',
      component: DevicesTab
    },
    {
      id: 'sessions',
      label: 'Sessions',
      icon: 'Activity',
      component: SessionsTab
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      component: PreferencesTab
    },
    {
      id: 'privacy',
      label: 'Privacy',
      icon: 'Eye',
      component: PrivacyTab
    }
  ];

  const ActiveComponent = tabs?.find(tab => tab?.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
      }`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="User" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
                <p className="text-muted-foreground">
                  Manage your security preferences, devices, and privacy settings
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Tab Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4">
                <nav className="space-y-2">
                  {tabs?.map((tab) => (
                    <Button
                      key={tab?.id}
                      variant={activeTab === tab?.id ? "default" : "ghost"}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full justify-start h-12 ${
                        activeTab === tab?.id 
                          ? 'bg-primary text-primary-foreground' 
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon name={tab?.icon} size={20} className="mr-3" />
                      <span className="flex-1 text-left">{tab?.label}</span>
                    </Button>
                  ))}
                </nav>

                {/* Account Info */}
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} color="white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">Admin User</p>
                      <p className="text-sm text-muted-foreground truncate">admin@securevault.com</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2 text-xs text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Plan:</span>
                      <span className="font-medium">Enterprise</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Member since:</span>
                      <span>Jan 2024</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Last login:</span>
                      <span>Today, 2:30 PM</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="lg:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6">
                {/* Mobile Tab Header */}
                <div className="lg:hidden mb-6">
                  <div className="flex items-center space-x-3">
                    <Icon 
                      name={tabs?.find(tab => tab?.id === activeTab)?.icon} 
                      size={20} 
                      className="text-primary" 
                    />
                    <h2 className="text-xl font-semibold text-foreground">
                      {tabs?.find(tab => tab?.id === activeTab)?.label}
                    </h2>
                  </div>
                </div>

                {/* Tab Content */}
                {ActiveComponent && <ActiveComponent />}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettings;